import React from 'react';
import PencilIcon from 'shared/icons/PencilIcon';
import MessageMediaGallery from './MessageMediaGallery';

const HeartIcon = ({ filled, className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const TrashIcon = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const FlagIcon = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const MessageItem = ({
  message,
  conversation,
  translationState,
  onToggleTranslation,
  shouldOfferTranslation,
  onToggleLike,
  editingMessageId,
  editingContent,
  onEditingContentChange,
  onStartEditMessage,
  onCancelEditMessage,
  onSaveEditMessage,
  deletingMessageId,
  onRequestDeleteMessage,
  onCancelDeleteMessage,
  onConfirmDeleteMessage,
  isDeleting,
  isSavingEdit,
  onReportMessage,
  formatMessageDate,
  formatClientLabel,
}) => {
  const isOwnMessage = message.senderId === conversation.modelId;
  const isEditing = editingMessageId === message._id;
  const isDeletingCurrent = deletingMessageId === message._id;
  const isInteracting = isEditing || isDeletingCurrent;
  const translationLoading = translationState?.loading;
  const showTranslation =
    translationState?.showingTranslation && translationState?.translatedText;
  const displayText = showTranslation
    ? translationState?.translatedText
    : message.message;
  const hasText = typeof displayText === 'string' && displayText.trim().length > 0;
  const rowClassNames = [
    'message-row',
    isOwnMessage ? 'model' : 'fan',
    isInteracting ? 'editing-mode' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClassNames}>
      {!isOwnMessage ? (
        <button
          className={`like-button ${message.isLiked ? 'liked' : ''}`}
          title={message.isLiked ? 'Retirer le like' : 'Liker'}
          aria-label={message.isLiked ? 'Retirer le like' : 'Liker'}
          onClick={() => onToggleLike(message)}
          disabled={Boolean(message.__liking)}
        >
          <HeartIcon className="heart-icon" filled={Boolean(message.isLiked)} />
        </button>
      ) : (
        message.isLiked && (
          <div className="like-button liked passive" title="Aimé" aria-label="Aimé">
            <HeartIcon className="heart-icon" filled />
          </div>
        )
      )}

      <div className="message-bubble">
        {isOwnMessage && !isInteracting && (
          <div className="message-actions">
            <button
              className="action-icon-button"
              title="Modifier"
              aria-label="Modifier le message"
              onClick={() => onStartEditMessage(message)}
            >
              <PencilIcon className="action-icon" />
            </button>
            <button
              className="action-icon-button danger"
              title="Supprimer"
              aria-label="Supprimer le message"
              onClick={() => onRequestDeleteMessage(message)}
            >
              <TrashIcon className="action-icon" />
            </button>
            {message.client && message.client.toLowerCase() === 'unbot' && (
              <button
              className="action-icon-button warning"
              title="Signaler"
              aria-label="Signaler le message"
              onClick={() => onReportMessage(message)}
            >
              <FlagIcon className="action-icon" />
            </button>
            )}
          </div>
        )}

        {isEditing ? (
          <div className="edit-container">
            <textarea
              className="edit-textarea"
              value={editingContent}
              onChange={(e) => onEditingContentChange(e.target.value)}
              rows={3}
            />
            <div className="edit-actions">
              <button
                className="btn btn-secondary"
                onClick={onCancelEditMessage}
                disabled={isSavingEdit}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onSaveEditMessage(message)}
                disabled={
                  isSavingEdit || editingContent.trim().length === 0
                }
              >
                {isSavingEdit ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {hasText && <p className="message-text">{displayText}</p>}
            {shouldOfferTranslation(message) && (
              <button
                className="translation-toggle"
                onClick={() => onToggleTranslation(message)}
                disabled={translationLoading}
              >
                {translationLoading
                  ? 'Traduction...'
                  : showTranslation
                  ? "Voir l'original"
                  : 'Voir la traduction'}
              </button>
            )}
          </>
        )}

        <MessageMediaGallery mediaList={message.mediaPrivateList} />

        {isDeletingCurrent && (
          <div className="delete-confirmation">
            {isDeleting ? (
              <span className="delete-loading">Suppression...</span>
            ) : (
              <>
                <span>Supprimer ce message ?</span>
                <div className="delete-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={onCancelDeleteMessage}
                    disabled={isDeleting}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => onConfirmDeleteMessage(message)}
                    disabled={isDeleting}
                  >
                    Supprimer
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <span className="message-date">
          {formatMessageDate(message.date)}
          {isOwnMessage && message.client && (
            <span className="message-client">
              {' '}
              · via {formatClientLabel(message.client)}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default MessageItem;


